import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const sections = [
  {
    title: "1. Information We May Collect",
    blocks: [
      { type: "p", text: "We may collect information that you voluntarily provide, including:" },
      {
        type: "ul",
        items: [
          "Your name",
          "Business or company name",
          "Email address",
          "Telephone number, if provided",
          "Information you provide when scheduling a meeting",
          "Information you include in messages, forms, or discovery-call responses",
          "Answers you provide in interactive assessments (such as the Lead Leakage Scorecard), along with the scores and recommendations generated from them, and — only if you voluntarily provide it — the email address used to send you your results",
        ],
      },
      {
        type: "p",
        text: "We may also receive limited technical information about use of the website, such as browser type, device type, general location, referring page, and website activity, through our website hosting or analytics providers.",
      },
    ],
  },
  {
    title: "2. Scheduling Through Calendly",
    blocks: [
      { type: "p", text: "iVIBES Consulting uses Calendly to allow visitors to schedule discovery calls." },
      {
        type: "p",
        text: "When you use the scheduling feature, information you provide may be processed by Calendly in accordance with Calendly's own privacy practices.",
      },
      {
        type: "p",
        text: "Information submitted through Calendly may include your name, email address, scheduling preferences, and responses to optional booking questions.",
      },
    ],
  },
  {
    title: "3. How We Use Information",
    blocks: [
      { type: "p", text: "Information collected through the website may be used to:" },
      {
        type: "ul",
        items: [
          "Respond to inquiries",
          "Schedule and conduct discovery calls",
          "Communicate with prospective or current clients",
          "Understand business needs",
          "Improve our website, services, and customer experience",
          "Maintain appropriate business records",
          "Protect the website and our business from misuse or security threats",
          "Comply with legal or regulatory requirements",
        ],
      },
      { type: "p", text: "We do not sell personal information to advertisers." },
    ],
  },
  {
    title: "4. Email Communications",
    blocks: [
      {
        type: "p",
        text: "If you contact iVIBES Consulting or schedule a meeting, we may use your email address to communicate with you about your inquiry, appointment, or potential business relationship.",
      },
      {
        type: "p",
        text: "We will not intentionally add you to unrelated promotional email campaigns solely because you requested a discovery call.",
      },
      {
        type: "p",
        text: "If you complete the Lead Leakage Scorecard and ask for your results by email, we will use your email address only to deliver that report. Any optional marketing communications require a separate, explicit opt-in, which is unchecked by default and never required to receive your report.",
      },
      {
        type: "p",
        text: "If we introduce marketing email communications in the future, appropriate unsubscribe options will be provided.",
      },
    ],
  },
  {
    title: "5. Text Messages",
    blocks: [
      {
        type: "p",
        text: "The current website does not enroll visitors in recurring promotional text-message campaigns simply by booking a discovery call.",
      },
      {
        type: "p",
        text: "If SMS or text-message marketing is introduced in the future, iVIBES Consulting will obtain appropriate consent and provide required opt-out mechanisms before using telephone numbers for recurring marketing communications.",
      },
    ],
  },
  {
    title: "6. Cookies and Similar Technologies",
    blocks: [
      {
        type: "p",
        text: "The website and third-party service providers may use cookies or similar technologies that are necessary for website functionality, security, scheduling, analytics, or performance.",
      },
      {
        type: "p",
        text: "If additional advertising or tracking technologies are introduced in the future, this Privacy Policy may be updated accordingly.",
      },
    ],
  },
  {
    title: "7. Third-Party Service Providers",
    blocks: [
      {
        type: "p",
        text: "iVIBES Consulting may rely on third-party providers for services such as:",
      },
      {
        type: "ul",
        items: [
          "Website hosting",
          "Appointment scheduling",
          "Email",
          "Video conferencing",
          "Analytics",
          "Security and website performance",
        ],
      },
      {
        type: "p",
        text: "These providers may process limited personal information on our behalf in order to provide their services.",
      },
      {
        type: "p",
        text: "iVIBES Consulting does not control the privacy practices of third-party websites or services and encourages visitors to review their applicable privacy policies.",
      },
    ],
  },
  {
    title: "8. Information Sharing",
    blocks: [
      {
        type: "p",
        text: "We may share information with service providers when reasonably necessary to operate the website, schedule meetings, communicate with you, or provide requested services.",
      },
      {
        type: "p",
        text: "We may also disclose information when required by law, legal process, or a legitimate governmental request, or when reasonably necessary to protect our rights, users, business, or systems.",
      },
      { type: "p", text: "We do not sell or rent personal information for monetary consideration." },
    ],
  },
  {
    title: "9. Data Security",
    blocks: [
      {
        type: "p",
        text: "We take reasonable steps to protect information under our control.",
      },
      {
        type: "p",
        text: "However, no website, electronic communication, or online system can be guaranteed to be completely secure. Visitors should avoid submitting highly sensitive or confidential information through general website forms or scheduling tools.",
      },
    ],
  },
  {
    title: "10. Data Retention",
    blocks: [
      {
        type: "p",
        text: "We may retain contact and business information for as long as reasonably necessary to:",
      },
      {
        type: "ul",
        items: [
          "Respond to inquiries",
          "Maintain business records",
          "Support an existing or potential client relationship",
          "Meet legal, tax, regulatory, or security obligations",
        ],
      },
      {
        type: "p",
        text: "Information that is no longer reasonably required may be deleted or anonymized where appropriate.",
      },
      {
        type: "p",
        text: "Lead Leakage Scorecard submissions are retained for limited, defined periods:",
      },
      {
        type: "ul",
        items: [
          "Anonymous assessments (no email address provided): up to 30 days.",
          "Identified assessments without marketing consent: up to 90 days, unless an active client relationship or another documented retention requirement applies.",
          "Assessments with marketing consent: a minimal contact record (name, email address, business type, consent status, and overall score) may be retained while you remain subscribed, but the detailed assessment answers are deleted after 90 days.",
        ],
      },
      {
        type: "p",
        text: "Retention cleanup runs automatically on a daily schedule. You may request earlier deletion of your information at any time using the contact details below.",
      },
    ],
  },
  {
    title: "11. Your Choices",
    blocks: [
      {
        type: "p",
        text: "You may contact us to request that we update or delete personal information that you have provided directly to iVIBES Consulting, subject to any legitimate business or legal retention requirements.",
      },
      { type: "p", text: "Requests may be sent to:" },
      { type: "email", text: "ismail@ivibesconsulting.com" },
    ],
  },
  {
    title: "12. Children's Privacy",
    blocks: [
      {
        type: "p",
        text: "The iVIBES Consulting website and services are intended for businesses and adult users.",
      },
      {
        type: "p",
        text: "We do not knowingly collect personal information from children through this website.",
      },
    ],
  },
  {
    title: "13. Changes to This Policy",
    blocks: [
      {
        type: "p",
        text: "We may update this Privacy Policy as our website, services, or legal obligations change.",
      },
      {
        type: "p",
        text: "The effective date shown at the top of this page will indicate when the policy was most recently revised.",
      },
    ],
  },
  {
    title: "14. Contact",
    blocks: [
      { type: "p", text: "Questions regarding this Privacy Policy may be directed to:" },
      { type: "p", text: "iVIBES Consulting" },
      { type: "email", text: "ismail@ivibesconsulting.com" },
    ],
  },
];

function Block({ block }) {
  if (block.type === "ul") {
    return (
      <ul className="list-disc pl-6 space-y-2 text-slate-300 leading-relaxed">
        {block.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  }
  if (block.type === "email") {
    return (
      <p className="text-slate-300 leading-relaxed">
        Email:{" "}
        <a
          href={`mailto:${block.text}`}
          className="text-cyan-300 font-semibold hover:text-cyan-200 transition-colors duration-200"
        >
          {block.text}
        </a>
      </p>
    );
  }
  return <p className="text-slate-300 leading-relaxed">{block.text}</p>;
}

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-page" className="min-h-screen bg-[#0B0F19]">
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" data-testid="privacy-logo" className="flex items-center gap-2.5">
            <span className="bg-white/95 rounded-xl px-2 py-1 flex items-center">
              <img src="/ivibes-cloud.png" alt="iVIBES logo" className="h-7 w-auto" />
            </span>
            <span className="font-heading text-xl tracking-tight font-extrabold">
              <span className="text-cyan-400">i</span>VIBES
              <span className="text-xl font-medium text-slate-200 ml-1.5 tracking-normal">Consulting</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-cyan-300 transition-colors duration-200"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Back to Home</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14 md:py-20">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Privacy Policy</h1>
        <p className="mt-3 text-sm text-slate-400">Effective Date: August 20, 2026</p>

        <p className="mt-8 text-slate-200 leading-relaxed">
          iVIBES Consulting respects your privacy and is committed to handling personal information
          responsibly.
        </p>
        <p className="mt-4 text-slate-200 leading-relaxed">
          This Privacy Policy explains what information may be collected when you visit the iVIBES
          Consulting website, contact us, or schedule a discovery call, and how that information may
          be used.
        </p>

        {sections.map((s) => (
          <section key={s.title} className="mt-10">
            <h2 className="font-heading font-bold text-xl text-white mb-4">{s.title}</h2>
            <div className="space-y-4">
              {s.blocks.map((b, i) => (
                <Block key={i} block={b} />
              ))}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
